import { AutomationDto } from "@/shared/dtos/automation-dto";
import { SortDirection } from "@/shared/enums/catalogue-search-filter-type";
import { StreamingSortByEnum } from "@/shared/enums/streaming-sort";
import { AutomationService } from "@/shared/services/automationService";
import { t } from "i18next";
import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import AutomationCardFilters from "../AutomationCardFilters/AutomationCardFilters";
import AutomationCardList from "../AutomationCardList/AutomationCardList";
import "./AutomationCards.scss";

const AUTOMATION_FILTERS_STORAGE_KEY = "automationFilters";

const saveFiltersToStorage = (filters: Record<string, any>) => {
	localStorage.setItem(AUTOMATION_FILTERS_STORAGE_KEY, JSON.stringify(filters));
};
const loadFiltersFromStorage = (): Record<string, any> => {
	try {
		const stored = localStorage.getItem(AUTOMATION_FILTERS_STORAGE_KEY);
		return stored ? JSON.parse(stored) : {};
	} catch {
		return {};
	}
};
export const clearAutomationFilters = () => {
	localStorage.removeItem(AUTOMATION_FILTERS_STORAGE_KEY);
};

export default function AutomationCards() {
	const [automations, setAutomations] = useState<AutomationDto[]>([]);
	const [filters, setFilters] = useState<Record<string, any>>(() =>
		loadFiltersFromStorage(),
	);
	const [pageIndex, setPageIndex] = useState(0);
	const [pageSize, setPageSize] = useState(12);
	const [totalItems, setTotalItems] = useState(0);
	const navigate = useNavigate();

	useEffect(() => saveFiltersToStorage(filters), [filters]);

	const fetchAutomations = useCallback(async () => {
		const res = await AutomationService.getAutomations({
			page: pageIndex,
			perPage: pageSize,
			sessionName: filters.sessionName || undefined,
			statuses: filters.statuses?.length ? filters.statuses : undefined,
			taskTypeId: filters.taskTypeId ?? undefined,
			technologyId: filters.technologyId ?? undefined,
			sortBy: filters.sortBy ?? StreamingSortByEnum.CREATED,
			sortDirection: filters.sortDirection ?? SortDirection.DESC,
		});

		const items = res.content;

		setAutomations(items);

		const total =
			filters.organizationName || filters.sourceType
				? items.length
				: (res.totalElements ?? items.length);
		setTotalItems(total);
	}, [
		pageIndex,
		pageSize,
		filters.sessionName,
		filters.statuses,
		filters.taskTypeId,
		filters.technologyId,
		filters.consolidationType,
		filters.consolidationValue,
		filters.sortBy,
		filters.sortDirection,
	]);

	useEffect(() => {
		fetchAutomations();
	}, [fetchAutomations]);

	const handleAction = useCallback(
		async (
			action: (sessionId: string) => Promise<void> | Promise<string>,
			a: AutomationDto,
		) => {
			await action(a.sessionId);
			await fetchAutomations();
		},
		[fetchAutomations],
	);

	const onViewDetail = useCallback(
		(a: AutomationDto) => {
			navigate(`/automations/${a.sessionId}`, { state: { fromList: true } });
		},
		[navigate],
	);

	const onPageIndexChanged = useCallback((newIndex: number) => {
		setPageIndex(newIndex - 1);
	}, []);
	const onPageSizeChanged = useCallback((newSize: number) => {
		setPageSize(newSize);
		setPageIndex(0);
	}, []);

	const handleClearSpecificFilter = useCallback((field: string) => {
		setFilters((prev) => {
			const next = { ...prev, [field]: null };
			saveFiltersToStorage(next);
			return next;
		});
	}, []);

	return (
		<>
			<div className="catalogue-title">
				<div className="row">
					<div className="col-md-12 col">
						<i className="mdi mdi-autorenew catalogue-book me-2" />
						<span className="catalogue-title-text">
							{t("automation.list-title")}
						</span>
					</div>
				</div>
			</div>

			<div className="content-block">
				<div className="row">
					<div className="catalogue-options-container">
						<AutomationCardFilters
							initialFilters={filters}
							onFilterChange={(f) => {
								setFilters(f);
								setPageIndex(0);
							}}
							onClearFilters={clearAutomationFilters}
							onClearSpecificFilter={handleClearSpecificFilter}
						/>
					</div>
				</div>
			</div>

			<div className="row">
				<div className="col-12">
					<div className="automation-list">
						<div className="content-block dx-card">
							<AutomationCardList
								automations={automations}
								totalItemCount={totalItems}
								pageIndex={pageIndex + 1}
								pageSize={pageSize}
								onPageIndexChanged={onPageIndexChanged}
								onPageSizeChanged={onPageSizeChanged}
								onPlay={(a) =>
									handleAction(AutomationService.startAutomation, a)
								}
								onPause={(a) =>
									handleAction(AutomationService.pauseAutomation, a)
								}
								onStop={(a) =>
									handleAction(AutomationService.stopAutomation, a)
								}
								onResume={(a) =>
									handleAction(AutomationService.startAutomation, a)
								}
								onViewDetail={onViewDetail}
							/>
						</div>
					</div>
				</div>
			</div>
		</>
	);
}
